import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { campeonatosService } from '../services/campeonatos.service'
import type { CampeonatoFilters } from '../types'
import { cacheCampeonatos, getCachedCampeonatos } from '../../../lib/offlineDb'

export const CAMPEONATOS_KEY = 'campeonatos'
export const REGRAS_VALOR_KEY = 'regras-valor'

export function useCampeonatos(filters: CampeonatoFilters = {}) {
  return useQuery({
    queryKey: [CAMPEONATOS_KEY, filters],
    queryFn: async () => {
      const data = await campeonatosService.list(filters)
      await cacheCampeonatos(data.data as unknown[])
      return data
    },
  })
}

export function useCachedCampeonatos() {
  return useQuery({
    queryKey: [CAMPEONATOS_KEY, 'cached'],
    queryFn: async () => {
      const cached = await getCachedCampeonatos()
      return cached as unknown[]
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export function useCampeonato(id: number | null) {
  return useQuery({
    queryKey: [CAMPEONATOS_KEY, id],
    queryFn: () => campeonatosService.getById(id!),
    enabled: id !== null,
  })
}

export function useCreateCampeonato() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: campeonatosService.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAMPEONATOS_KEY] }),
  })
}

export function useUpdateCampeonato() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => campeonatosService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAMPEONATOS_KEY] }),
  })
}

export function useDeleteCampeonato() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => campeonatosService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAMPEONATOS_KEY] }),
  })
}

export function useRegrasValor(idcampeonato: number | null) {
  return useQuery({
    queryKey: [REGRAS_VALOR_KEY, idcampeonato],
    queryFn: () => campeonatosService.listRegras(idcampeonato!),
    enabled: idcampeonato !== null,
  })
}

export function useCreateRegraValor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ idcampeonato, data }: { idcampeonato: number; data: any }) =>
      campeonatosService.createRegra(idcampeonato, data),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: [REGRAS_VALOR_KEY, vars.idcampeonato] }),
  })
}

export function useDeleteRegraValor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, idcampeonato }: { id: number; idcampeonato: number }) =>
      campeonatosService.deleteRegra(id),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: [REGRAS_VALOR_KEY, vars.idcampeonato] }),
  })
}
